import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import {
  UserIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, searchTerm, statusFilter, roleFilter]);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(roleFilter && { role: roleFilter }),
      });

      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setPagination((prev) => ({
        ...prev,
        total: response.data.total,
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { status });
      toast.success(`User status updated to ${status}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setEditingUser(null);
    setShowUserModal(true);
  };

  const editUser = (user) => {
    setEditingUser(user);
    setSelectedUser(null);
    setShowUserModal(true);
  };

  const saveUserEdit = async (userData) => {
    try {
      await api.put(`/admin/users/${userData.id}`, userData);
      toast.success('User updated successfully');
      setShowUserModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getRoleBadge = (role) => {
    const badges = {
      owner: 'bg-primary-100 text-primary-800',
      admin: 'bg-purple-100 text-purple-800',
      member: 'bg-blue-100 text-blue-800',
    };
    return badges[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="page-title">Manage Users</h1>
            <p className="text-sm text-neutral-500">
              View and manage user accounts ({pagination.total} total users)
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="input-field"
          >
            <option value="">All Roles</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="member">Member</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setRoleFilter('');
            }}
            className="btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="card p-0">
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">User</th>
                <th className="table-header-cell">Organization</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Role</th>
                <th className="table-header-cell">Joined</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {users.map((u) => (
                <tr key={u.id} className="table-row-hover">
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-white">
                          {u.firstName?.charAt(0)}{u.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-800">
                          {u.firstName} {u.lastName}
                        </p>
                        <p className="text-xs text-neutral-500">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell text-sm text-neutral-600">
                    {u.tenantName || '—'}
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(u.status)}`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadge(u.role)}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="table-cell text-sm text-neutral-500">
                    {formatDate(u.createdAt)}
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openUserDetails(u)}
                        className="p-1 text-blue-500 hover:text-blue-700 transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => editUser(u)}
                        className="p-1 text-green-500 hover:text-green-700 transition-colors"
                        title="Edit User"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {u.status === 'active' ? (
                        <button
                          onClick={() => updateUserStatus(u.id, 'suspended')}
                          className="p-1 text-yellow-500 hover:text-yellow-700 transition-colors"
                          title="Suspend User"
                        >
                          <UserMinusIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => updateUserStatus(u.id, 'active')}
                          className="p-1 text-green-500 hover:text-green-700 transition-colors"
                          title="Activate User"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteUser(u.id)}
                        className="p-1 text-red-400 hover:text-red-600 transition-colors"
                        title="Delete User"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 flex items-center justify-between border-t border-neutral-200">
            <p className="text-sm text-neutral-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn-secondary btn-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page * pagination.limit >= pagination.total}
                className="btn-secondary btn-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">
              {editingUser ? 'Edit User' : 'User Details'}
            </h3>

            {editingUser ? (
              <UserEditForm
                user={editingUser}
                onSave={saveUserEdit}
                onCancel={() => {
                  setShowUserModal(false);
                  setEditingUser(null);
                }}
              />
            ) : (
              <UserDetailsView
                user={selectedUser}
                onClose={() => setShowUserModal(false)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserDetailsView = ({ user, onClose }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Name</label>
          <p className="mt-1 text-sm text-neutral-800">{user.firstName} {user.lastName}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Email</label>
          <p className="mt-1 text-sm text-neutral-800">{user.email}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Phone</label>
          <p className="mt-1 text-sm text-neutral-800">{user.phone || 'Not provided'}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Organization</label>
          <p className="mt-1 text-sm text-neutral-800">{user.tenantName || 'Not assigned'}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Status</label>
          <p className="mt-1 text-sm text-neutral-800 capitalize">{user.status}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Role</label>
          <p className="mt-1 text-sm text-neutral-800 capitalize">{user.role}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Joined</label>
          <p className="mt-1 text-sm text-neutral-800">{new Date(user.createdAt).toLocaleDateString()}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 uppercase">Email Verified</label>
          <p className="mt-1 text-sm text-neutral-800">{user.emailVerified ? 'Yes' : 'No'}</p>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button onClick={onClose} className="btn-secondary">
          Close
        </button>
      </div>
    </div>
  );
};

const UserEditForm = ({ user, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone || '',
    status: user.status,
    role: user.role,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...user, ...formData });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="input-label">First Name</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="input-label">Last Name</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="input-label">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="input-label">Phone</label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
            className="input-field"
          />
        </div>
        <div>
          <label className="input-label">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
            className="input-field"
          >
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div>
          <label className="input-label">Role</label>
          <select
            value={formData.role}
            onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
            className="input-field"
          >
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="member">Member</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary">
          Save Changes
        </button>
      </div>
    </form>
  );
};

export default AdminUsers;
